/**
 * Ask AI: the in-app guide.
 *
 * A small conversation that drops from its button in the top strip, on every
 * page. It answers "where is…" and "how do I…" and links to the page; it is
 * not Eve and does not see the company's data — for that it sends people to
 * chat. The conversation lives as long as the tab does, so closing the panel
 * to follow a link and opening it again picks up where it left off.
 *
 * Answers render through the main chat's markdown renderer, loaded lazily so
 * the strip stays light until the panel is first opened.
 *
 * @module components/guide/AskAiPanel
 * @see apps/api/src/guide.ts for what the guide knows and will not say
 */
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowUpIcon, SquareIcon, WandSparklesIcon } from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
} from "react";

import { useAtomValue } from "@effect/atom-react";

import { onOpenGuide, runGuideShortcutClaim, useGuideShortcutClaimed } from "../../guideBus";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../../keybindings";
import { isTerminalFocused } from "../../lib/terminalFocus";
import { cn } from "../../lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import { useAccountProfile } from "../../state/accountProfile";
import { askGuide, GuideAskError, type GuideTurn } from "../../state/guideAsk";
import { useActiveOrgId } from "../../state/orgs";
import { primaryServerKeybindingsAtom } from "../../state/server";
import { AMOREG_MARK_BODY_PATH, AMOREG_MARK_LEG_PATH } from "../sidebar/SidebarChrome";
import { Button } from "../ui/button";
import { Kbd } from "../ui/kbd";
import { Message, MessageContent, MessageScroller } from "../ui/message";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";

const ChatMarkdown = lazy(() => import("../ChatMarkdown"));

/**
 * The chat renderer reads a bare `/path` as a file on disk. The guide's in-app
 * links are made absolute under this origin instead, so they render as links;
 * {@link inAppPathOf} turns a click on one back into a route.
 */
export function absolutizeInAppLinks(markdown: string, origin: string): string {
  return markdown.replace(/\]\((\/[^)\s]*)\)/g, (_match, path: string) => `](${origin}${path})`);
}

/** The route a same-origin link points at, or null for anywhere else. */
export function inAppPathOf(href: string, origin: string): string | null {
  try {
    const url = new URL(href, origin);
    return url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}

const FAILURE_TEXT: Record<"rate-limited" | "unavailable", string> = {
  "rate-limited": "That's a lot of questions at once. Give it a few seconds and ask again.",
  unavailable: "The guide isn't available right now. Please try again in a moment.",
};

const SHORTCUT_CHIP_CLASS =
  "h-4 min-w-0 shrink-0 rounded-sm bg-sidebar-control-surface px-1.5 text-[10px] text-sidebar-muted-foreground ring-1 ring-sidebar-border";

export function AskAiPanel({ triggerClassName }: { readonly triggerClassName?: string }) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const organizationId = useActiveOrgId();
  const { account } = useAccountProfile();
  const firstName = account?.firstName?.trim() || null;

  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<ReadonlyArray<GuideTurn>>([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const busy = streaming !== null;

  // A rebindable command (guide.toggle), so it shows on the shortcuts page and
  // the chip always names the key actually bound.
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const shortcutLabel = shortcutLabelForCommand(keybindings, "guide.toggle");
  const shortcutClaimed = useGuideShortcutClaimed();
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.target instanceof HTMLElement && event.target.closest("[data-keybinding-capture]")) {
        return;
      }
      // A focused terminal keeps its own Mod+J: the rule is `when: !terminalFocus`.
      const command = resolveShortcutCommand(event, keybindings, {
        context: { terminalFocus: isTerminalFocused() },
      });
      if (command !== "guide.toggle") return;
      event.preventDefault();
      event.stopPropagation();
      // An open guide closes first: the person opened it from the chip, whatever page holds the key.
      if (!openRef.current && runGuideShortcutClaim()) return;
      setOpen((value) => !value);
    };
    // Capture: ahead of editors and panes that would otherwise consume it.
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [keybindings]);

  // The help menu, and anything else, can open the guide by asking.
  useEffect(() => onOpenGuide(() => setOpen(true)), []);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => () => inFlight.current?.abort(), []);

  const ask = useCallback(
    async (question: string) => {
      if (!organizationId) return;
      const next: GuideTurn[] = [...turns, { role: "user", content: question }];
      setTurns(next);
      setDraft("");
      setFailure(null);
      setStreaming("");
      const controller = new AbortController();
      inFlight.current = controller;
      try {
        const answer = await askGuide(
          {
            organizationId,
            orgSegment: orgUrlSegment(organizationId),
            path: pathname,
            messages: next,
          },
          controller.signal,
          setStreaming,
        );
        setTurns([...next, { role: "assistant", content: answer }]);
      } catch (error) {
        if (!controller.signal.aborted) {
          setFailure(FAILURE_TEXT[error instanceof GuideAskError ? error.reason : "unavailable"]);
        }
      } finally {
        if (inFlight.current === controller) inFlight.current = null;
        setStreaming(null);
      }
    },
    [organizationId, pathname, turns],
  );

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    const question = draft.trim();
    if (question.length === 0 || busy) return;
    void ask(question);
  };

  const stop = () => {
    const partial = streaming;
    inFlight.current?.abort();
    // Keep what was already written: a stopped answer is still an answer.
    if (partial && partial.trim().length > 0) {
      setTurns((current) => [...current, { role: "assistant", content: partial }]);
    }
  };

  const clear = () => {
    inFlight.current?.abort();
    setTurns([]);
    setFailure(null);
    setDraft("");
    inputRef.current?.focus();
  };

  // In-app links route inside the app and get out of the way; others open as usual.
  const onLinkClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    const anchor = (event.target as HTMLElement).closest("a[href]");
    if (!anchor) return;
    const path = inAppPathOf(anchor.getAttribute("href") ?? "", window.location.origin);
    if (path === null) return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    void navigate({ to: path } as never);
  };

  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const isEmpty = turns.length === 0 && !busy;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={cn("cursor-pointer gap-1.5", triggerClassName)}
          />
        }
      >
        <WandSparklesIcon aria-hidden />
        Ask AI
        {shortcutLabel && !shortcutClaimed ? <Kbd className={SHORTCUT_CHIP_CLASS}>{shortcutLabel}</Kbd> : null}
      </PopoverTrigger>
      <PopoverPopup
        side="bottom"
        align="end"
        sideOffset={8}
        aria-label="Ask AI"
        className="w-[min(26rem,calc(100vw-1.5rem))] rounded-xl"
        viewportClassName="overflow-hidden! p-0 [--viewport-inline-padding:0px]"
      >
        {/* The popover wraps its content, so the column owns its own height:
            header, a scroller that takes what is left, the composer last. */}
        <div className="flex h-[min(38rem,calc(100dvh-5rem))] w-full flex-col">
        <div className="flex h-10 shrink-0 items-center justify-between ps-4 pe-2">
          <span className="text-xs font-medium text-muted-foreground">Guide</span>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="cursor-pointer text-muted-foreground"
            disabled={turns.length === 0 && !busy && failure === null}
            onClick={clear}
          >
            Clear chat
          </Button>
        </div>

        {isEmpty ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
            <GuideMark className="mb-2 h-9 w-auto text-foreground" />
            <p className="text-sm font-medium text-foreground">
              {firstName ? `Hi ${firstName}, how can I help?` : "How can I help?"}
            </p>
            <p className="text-sm text-muted-foreground">
              Ask where something is or how to do it. I'll point you to the page.
            </p>
            {failure ? <p className="mt-2 text-sm text-destructive-foreground">{failure}</p> : null}
          </div>
        ) : (
          <MessageScroller
            follow={`${turns.length}:${streaming?.length ?? 0}`}
            className="px-4 pb-2"
            onClickCapture={onLinkClick}
          >
            <div className="flex flex-col gap-4 py-1">
              {turns.map((turn, index) => (
                <Message key={index} from={turn.role}>
                  <MessageContent>
                    {turn.role === "user" ? (
                      <p className="whitespace-pre-wrap break-words">{turn.content}</p>
                    ) : (
                      <GuideMarkdown text={absolutizeInAppLinks(turn.content, origin)} />
                    )}
                  </MessageContent>
                </Message>
              ))}
              {busy ? (
                <Message from="assistant">
                  <MessageContent>
                    {streaming && streaming.length > 0 ? (
                      <GuideMarkdown text={absolutizeInAppLinks(streaming, origin)} streaming />
                    ) : (
                      <span role="status" className="inline-flex items-center gap-1 text-muted-foreground">
                        <span className="sr-only">Thinking</span>
                        {[0, 1, 2].map((dot) => (
                          <span
                            key={dot}
                            aria-hidden
                            className="size-1.5 animate-pulse rounded-full bg-current"
                            style={{ animationDelay: `${dot * 160}ms` }}
                          />
                        ))}
                      </span>
                    )}
                  </MessageContent>
                </Message>
              ) : null}
              {failure ? <p className="text-sm text-destructive-foreground">{failure}</p> : null}
            </div>
          </MessageScroller>
        )}

        <form onSubmit={submit} className="shrink-0 p-3 pt-1">
          <div className="flex items-end gap-2 rounded-xl border border-input bg-background px-3 py-2 focus-within:border-ring">
            {/* Native on purpose: the kit's Textarea has a tall minimum, and this
                is a one-line composer that grows with what is typed. */}
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              maxLength={4000}
              onChange={(event) => setDraft(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  submit();
                }
              }}
              placeholder={organizationId ? "Ask how to do something…" : "Choose a company first"}
              disabled={!organizationId}
              aria-label="Ask the guide"
              className="field-sizing-content max-h-32 min-h-6 flex-1 resize-none bg-transparent text-sm leading-6 outline-none placeholder:text-muted-foreground"
            />
            {busy ? (
              <Button type="button" size="icon-xs" variant="secondary" aria-label="Stop" onClick={stop} className="cursor-pointer">
                <SquareIcon className="size-3 fill-current" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon-xs"
                aria-label="Send"
                disabled={draft.trim().length === 0 || !organizationId}
                className="cursor-pointer"
              >
                <ArrowUpIcon />
              </Button>
            )}
          </div>
        </form>
        </div>
      </PopoverPopup>
    </Popover>
  );
}

/**
 * The Amoreg mark for the guide's welcome: the body in ink, the leg in brand
 * red. The leg runs on from the left, rests, and runs off to the right, on a
 * loop — a clip window sliding across it, so the shape itself never distorts.
 * The motion is CSS (`guide-leg-run` in index.css) and stops for reduced motion,
 * leaving the leg simply shown.
 */
function GuideMark({ className }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 73 74" xmlns="http://www.w3.org/2000/svg" className={className}>
      <defs>
        <clipPath id="guide-mark-leg-window">
          <rect className="guide-leg-window" x="48" y="57" width="27" height="19" />
        </clipPath>
      </defs>
      <path fillRule="evenodd" clipRule="evenodd" d={AMOREG_MARK_BODY_PATH} fill="currentColor" />
      <path d={AMOREG_MARK_LEG_PATH} fill="var(--brand-red, #de5656)" clipPath="url(#guide-mark-leg-window)" />
    </svg>
  );
}

/** The main chat's renderer, so an answer reads and streams the same way here. */
function GuideMarkdown({ text, streaming = false }: { text: string; streaming?: boolean }) {
  return (
    <Suspense fallback={<p className="whitespace-pre-wrap break-words">{text}</p>}>
      <ChatMarkdown text={text} cwd={undefined} isStreaming={streaming} className="text-sm leading-6" />
    </Suspense>
  );
}
